import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Button } from '@ets/design-system';

import { toRoman } from 'utils';
import { toast } from 'utils/toast';

import { setNoteOverlayData } from 'Editor/redux/NotesSlice';
import EditorManager from 'Editor/services/EditorManager';
import { usePublicProfile } from '_common/hooks';

import { Card, SuiteAvatar } from '_common/suite/components';
import { Textarea, type TextareaAPI, type TextareaProps } from '_common/lib/components';

import styles from './ViewNoteCard.module.scss';

type EditableNoteCardProps = {
  operation: string;
  note: Editor.Data.Notes.NoteData;
  testId: string;
  type: 'endnote' | 'footnote';
  selected?: boolean;
  sidebar?: boolean;
  user: UserId;
};

const EditableNoteCard = ({
  operation,
  note,
  testId,
  type,
  sidebar,
  selected,
  user,
}: EditableNoteCardProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const manager = EditorManager.getInstance();
  const profile = usePublicProfile(user);
  const [content, setContent] = useState(operation === 'edit' ? note.content : '');
  const author = usePublicProfile(note?.author);
  const textareaRef = useRef<TextareaAPI>(null);

  useEffect(() => {
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 0);
  }, []);

  const handleNoteValueChanged: TextareaProps['onChange'] = (e) => {
    const target = e.target as HTMLTextAreaElement;
    setContent(target.value);
  };
  const handleInsertNote = async () => {
    if (content) {
      await manager.insertNote(type, content);
    }
    handleCancelEdition();
  };

  const handleSaveClicked = async () => {
    if (note?.id) {
      await manager.editNote(note.id, content).then(() => {
        switch (type) {
          case 'endnote':
            toast({
              variant: 'success',
              title: 'ENDNOTES_EDITED',
              message: 'THE_NUMBERING_OF_ENDNOTES_WAS_EDITED',
            });
            break;
          case 'footnote':
            toast({
              variant: 'success',
              title: 'FOOTNOTES_EDITED',
              message: 'THE_NUMBERING_OF_FOOTNOTES_WAS_EDITED',
            });
            break;
        }
      });
    }
    handleCancelEdition();
  };

  const handleCancelEdition = () => {
    dispatch(
      setNoteOverlayData(operation === 'create' ? { offsets: null } : { operation: 'view' }),
    );
  };

  return (
    <Card testId={testId} flat={sidebar} selected={selected}>
      <Card.Header>
        <div style={{ display: 'flex' }}>
          <SuiteAvatar
            margin="0 1rem 0 0"
            userId={user}
            name={operation === 'edit' ? author.name : undefined}
          />
          <div className={styles.labels}>
            <div className={styles.author}>{profile.name}</div>
            <div className={styles.order}>
              {intl.formatMessage({ id: type?.toUpperCase() })}
              {note && ` #${type === 'footnote' ? note.order : toRoman(note.order)}`}
            </div>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        <Textarea
          plainText
          type="text"
          size="m"
          name="comment"
          placeholder={intl.formatMessage({
            id:
              type === 'footnote'
                ? 'NEW_FOOTNOTE_INPUT_PLACEHOLDER'
                : 'NEW_ENDNOTE_INPUT_PLACEHOLDER',
          })}
          onChange={handleNoteValueChanged}
          initialContent={content}
          ref={textareaRef}
          onKeyDown={(e) => {
            if (e.key === 'Escape' || e.code === 'Escape') {
              handleCancelEdition();
            }
          }}
          testId="temporary-note-card-textarea"
        />
      </Card.Body>
      <Card.Footer>
        <Card.Footer.Right>
          <Button
            variant="link"
            size="s"
            style={{ margin: '0 1rem 0 0' }}
            onClick={handleCancelEdition}
            testId="temporary-note-card-cancel"
          >
            <FormattedMessage id="global.cancel" />
          </Button>
          <Button
            variant="primary"
            size="s"
            onClick={operation === 'edit' ? () => handleSaveClicked() : () => handleInsertNote()}
            testId={`temporary-note-card-${operation === 'edit' ? 'save' : 'create'}`}
          >
            {operation === 'edit' ? (
              <FormattedMessage id="global.save" />
            ) : (
              <FormattedMessage id="global.create" />
            )}
          </Button>
        </Card.Footer.Right>
      </Card.Footer>
    </Card>
  );
};

export default EditableNoteCard;
